import React, { useState } from 'react'
import { Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { useTranslation } from '../hooks/use-i18n'
import roomsService from '../services/roomsService'
import { removeRoom, setSingleRoom } from '../store/roomsSlice'

interface DeleteRoomModalProps {
  roomId: string
  roomName?: string
  onClose: () => void
}

const DeleteRoomModal: React.FC<DeleteRoomModalProps> = ({
  roomId,
  roomName,
  onClose,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)

  const handleConfirm = async () => {
    setLoading(true)
    try {
      await roomsService.deleteRoom(roomId)
      roomsService.invalidateUserRoomsCache()
      dispatch(removeRoom(roomId))
      dispatch(setSingleRoom(null))
      await queryClient.invalidateQueries({ queryKey: ['user-rooms'] })
      toast.success(t('room.delete.success'))
      onClose()
      navigate('/')
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('room.delete.failed'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-2xl shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-3">
          <Trash2 className="w-5 h-5 text-destructive" />
          <h2 className="font-display text-xl font-bold text-foreground">
            {t('room.delete.title')}
          </h2>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          {t('room.delete.body', { room: roomName || t('room.leave.thisRoom') })}
        </p>
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted transition-colors"
          >
            {t('room.delete.cancel')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-destructive text-destructive-foreground hover:opacity-90 disabled:opacity-50"
          >
            {loading ? t('common.loading') : t('room.delete.confirm')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default DeleteRoomModal
